// ABOUTME: The event envelope the server publishes on the user's realtime channel (ADR-0001 §3.3).
// ABOUTME: Parses a publication into a typed event; unknown versions and types are ignored with a log.
import { createLogger } from "@/model/logger"

const logger = createLogger("Realtime")

/** Bumped by the server only when the envelope changes shape; older clients then ignore it. */
const EVENT_VERSION = 1

/** Days of one user's calendar changed; `from`/`to` are inclusive `yyyy-MM-dd`. */
export interface CalendarChangedEvent {
  type: "calendar.changed"
  userId: string
  from: string
  to: string
  /** Who wrote: provider / user / plan / menu / recalc / weather / sync. Informational. */
  source: string
}

/** Health metrics of one user changed inside the inclusive `from`..`to` days. Reserved: not published yet. */
export interface HealthChangedEvent {
  type: "health.changed"
  userId: string
  from: string
  to: string
}

/** What happened in a discussion; `read` and `removed` name no message. */
export type DiscussionChangeReason = "posted" | "edited" | "deleted" | "read" | "removed"

const DISCUSSION_REASONS: ReadonlySet<string> = new Set<DiscussionChangeReason>([
  "posted",
  "edited",
  "deleted",
  "read",
  "removed",
])
/** The reasons about one message, which carry its number. */
const MESSAGE_REASONS: ReadonlySet<string> = new Set<DiscussionChangeReason>(["posted", "edited", "deleted"])

/**
 * The discussion under one calendar item changed (gql-api docs/frontend-discussion.md §7). It says
 * what changed, not the content: the feed is read again through the API.
 */
export interface DiscussionChangedEvent {
  type: "discussion.changed"
  /** The item's owner. */
  userId: string
  /** The item's day, both of them. */
  from: string
  to: string
  itemId: string
  reason: DiscussionChangeReason
  /** The message's number, for posted, edited and deleted; null for read and removed. */
  seq: number | null
}

export type RealtimeEvent = CalendarChangedEvent | HealthChangedEvent | DiscussionChangedEvent

/** Event types the contract defines for later phases; they are skipped without a warning. */
const RESERVED_TYPES = new Set(["profile.changed", "job.changed", "chat.changed", "notification.created"])

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null
const isDay = (value: unknown): value is string => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)

/**
 * Turns a publication payload into an event, or null when the client should
 * not act on it. Unknown `v` and `type` are logged rather than thrown: the
 * server may ship a new version before this client is updated, and a bad
 * envelope must not take the channel down.
 */
export function parseRealtimeEvent(data: unknown): RealtimeEvent | null {
  if (!isRecord(data)) {
    logger.warn("Ignoring a publication that is not an event envelope", { data })
    return null
  }
  if (data.v !== EVENT_VERSION) {
    logger.warn("Ignoring an event of an unsupported version", { v: data.v, type: data.type })
    return null
  }
  const { type, userId, from, to, source } = data
  if (type === "discussion.changed") return parseDiscussionEvent(data)
  if (type !== "calendar.changed" && type !== "health.changed") {
    if (typeof type !== "string" || !RESERVED_TYPES.has(type))
      logger.warn("Ignoring an event of an unknown type", { type })
    return null
  }
  if (typeof userId !== "string" || !isDay(from) || !isDay(to)) {
    logger.warn("Ignoring a malformed event", { type, userId, from, to })
    return null
  }
  if (type === "health.changed") return { type, userId, from, to }
  return { type, userId, from, to, source: typeof source === "string" ? source : "" }
}

function parseDiscussionEvent(data: Record<string, unknown>): DiscussionChangedEvent | null {
  const { userId, from, to, itemId, reason, seq } = data
  if (typeof reason !== "string" || !DISCUSSION_REASONS.has(reason)) {
    logger.warn("Ignoring a discussion event of an unknown reason", { reason })
    return null
  }
  const isAboutMessage = MESSAGE_REASONS.has(reason)
  if (
    typeof userId !== "string" ||
    !isDay(from) ||
    !isDay(to) ||
    typeof itemId !== "string" ||
    (isAboutMessage && (typeof seq !== "number" || !Number.isInteger(seq)))
  ) {
    logger.warn("Ignoring a malformed discussion event", { userId, itemId, reason, seq })
    return null
  }
  return {
    type: "discussion.changed",
    userId,
    from,
    to,
    itemId,
    reason: reason as DiscussionChangeReason,
    seq: isAboutMessage ? (seq as number) : null,
  }
}
